import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Последний рубеж: ошибка в любом месте не должна оставлять белый экран.
 *
 * Белый экран — худший из возможных отказов. Человек не знает, что случилось,
 * что делать и сохранилось ли то, что он вводил; он перезагружает страницу,
 * получает то же самое и звонит. Сообщение с кнопкой отвечает на всё это сразу.
 *
 * Чаще всего сюда попадают из-за расхождения версий: страница в браузере уже
 * новая, а сервер ещё на старом коде и не отдаёт поле, которое она ждёт. Про
 * это и написано первой строкой — в девяти случаях из десяти помогает
 * обновление страницы или перезапуск сервера.
 */
interface State {
  error: Error | null
}

export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // В консоль — со стеком: это единственный след, по которому потом искать
    console.error('Сбой в интерфейсе:', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="crash">
        <h1>Что-то пошло не так</h1>
        <p>
          Страница не смогла отрисоваться. Данные при этом не потерялись — всё,
          что было сохранено, осталось на сервере.
        </p>
        <p className="hint">
          Чаще всего это значит, что программа обновилась, а сервер ещё работает
          на старой версии. Обновите страницу; если повторится — перезапустите
          сервер ярлыком «Перезапуск сервера» и сообщите нам текст ниже.
        </p>
        <div className="crash-actions">
          <button className="btn primary" onClick={() => window.location.reload()}>
            Обновить страницу
          </button>
          <button
            className="btn"
            onClick={() => {
              void navigator.clipboard
                .writeText(`${error.name}: ${error.message}\n${error.stack ?? ''}`)
                .catch(() => undefined)
            }}
          >
            Скопировать текст ошибки
          </button>
        </div>
        <pre className="crash-text">
          {error.name}: {error.message}
        </pre>
      </div>
    )
  }
}
